import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useDrawCentreOptions } from '../store';

import { useDrawStore } from '@apps/draw/store';
import { getEditorialsQuery } from '@shared/queries/editorial';
import { MatchStatus } from '@shared-types/match.types';
import { language } from '@utilities/browser';

export const useDrawVideoPlayerOptions = () => {
  const draw = useDrawStore((state) => state.draw);
  const {
    options: { videoObjectAvailable, defaultVideoPlayerOptions, videoObjectOptions },
  } = useDrawCentreOptions();

  const isDrawReplay = draw?.status?.eventStatus === MatchStatus.FINISHED && (draw.videoPath || videoObjectAvailable);

  const videoPlayerOptions = useMemo(() => {
    if (draw?.status?.videoStreamAvailable) {
      return {
        id: draw?.id,
        file: draw?.videoPath,
        mute: true,
        autostart: true,
        kind: 'Draw live',
        disableAutoPause: true,
      };
    }
    if (videoObjectAvailable) return videoObjectOptions;

    return {
      id: draw?.id,
      file: draw?.videoPath,
    };
  }, [draw?.status?.videoStreamAvailable, draw?.videoPath, draw?.id, videoObjectAvailable, videoObjectOptions]);

  const drawNoVideo = useMemo(
    () =>
      (draw?.status?.eventStatus !== MatchStatus.FINISHED && !draw?.status?.videoStreamAvailable) ||
      (draw?.status?.eventStatus === MatchStatus.FINISHED && videoPlayerOptions?.id && draw?.videoPath === ''),
    [draw?.status?.eventStatus, draw?.status?.videoStreamAvailable, draw?.videoPath, videoPlayerOptions?.id],
  );

  return {
    isDrawReplay,
    drawNoVideo,
    videoPlayerOptions: {
      ...defaultVideoPlayerOptions,
      ...videoPlayerOptions,
    },
  };
};

export const useLiveblog = () => {
  const draw = useDrawStore((state) => state.draw);

  const liveblogFilters = useMemo(() => {
    if (!draw?.rounds?.length) return null;

    return {
      type: 'liveblog',
      'param.attributes.footballEntities.round.roundId': `${draw.rounds[0].roundId}`,
      'param.attributes.liveblogData.lbType': 'DRAW',
      'param.attributes.main.season': draw.seasonYear,
      'param.attributes.language': language,
      aggregator: 'lightnodejson',
    };
  }, [draw.rounds, draw.seasonYear]);

  const { data: liveblog, isLoading } = useQuery({
    ...getEditorialsQuery(liveblogFilters),
    select: (data) => data?.[0],
    enabled: draw?.status?.commentaryStatus !== MatchStatus.UPCOMING,
  });

  return { liveblog, isLoading };
};
